import React, { useState, useEffect, useRef } from 'react';
import {
  A11Y_THEMES, A11Y_MIN, A11Y_MAX, loadA11y, saveA11y, syncA11yFromServer,
} from '../../utils/accessibility';
import './accessibility.css';

const STEP = 15;

const AccessibilityControl = () => {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(loadA11y);
  const btnRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    syncA11yFromServer().then((v) => { if (!cancelled && v) setS(v); });
    return () => { cancelled = true; };
  }, []);

  const update = (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    saveA11y(next);
  };

  const toggleEnabled = (enabled) => {
    update({ enabled });
    // A magnified page has little room for the sidebar, so tuck it away (the top bar button reopens it).
    if (enabled) window.dispatchEvent(new CustomEvent('mavis-a11y-collapse-sidebar'));
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); if (btnRef.current) btnRef.current.focus(); } };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open]);

  return (
    <div className="a11y-wrap">
      <button
        ref={btnRef} type="button" className="topbar-icon-btn a11y-toggle-btn"
        aria-expanded={open} aria-controls="a11y-panel" onClick={() => setOpen((v) => !v)}
        title="Accessibility settings"
      >
        <span aria-hidden="true" style={{ fontWeight: 700 }}>Aa</span>
        <span className="a11y-toggle-text"> Accessibility{s.enabled ? ' (on)' : ''}</span>
      </button>
      {open && (
        <div id="a11y-panel" role="group" aria-label="Accessibility settings" className="a11y-panel">
          <label className="a11y-row a11y-main">
            <input type="checkbox" checked={s.enabled} onChange={(e) => toggleEnabled(e.target.checked)} />
            <span>Accessible view (larger page, high contrast)</span>
          </label>

          <fieldset className="a11y-fieldset" disabled={!s.enabled}>
            <legend>Colour theme</legend>
            {A11Y_THEMES.map((t) => (
              <label key={t.id} className="a11y-row">
                <input type="radio" name="a11y-theme" checked={s.theme === t.id} onChange={() => update({ theme: t.id })} />
                <span>{t.label}</span>
              </label>
            ))}
          </fieldset>

          <div className="a11y-row" role="group" aria-label="Page size">
            <span>Size: <strong aria-live="polite">{s.scale}%</strong></span>
            <button type="button" className="a11y-btn" disabled={!s.enabled || s.scale <= A11Y_MIN}
              onClick={() => update({ scale: Math.max(A11Y_MIN, s.scale - STEP) })} aria-label="Make everything smaller">A−</button>
            <button type="button" className="a11y-btn" disabled={!s.enabled || s.scale >= A11Y_MAX}
              onClick={() => update({ scale: Math.min(A11Y_MAX, s.scale + STEP) })} aria-label="Make everything larger">A+</button>
          </div>

          <p className="a11y-note">Saved to your account, so it follows you to other devices. Press Escape to close.</p>
        </div>
      )}
    </div>
  );
};

export default AccessibilityControl;
